import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { GrantPermissionRequest, ProvisionMailboxRequest, UpdateMailboxSettingsRequest } from "@onemileauto/shared-types";
import {
  grantPermission,
  listAllUsers,
  listPermissions,
  provisionMailbox,
  revokePermission,
  updateMailboxSettings,
} from "../lib/api-client";
import { mailboxQueryKeys } from "../lib/query-keys";

/** Owner-only mailbox provisioning (see routes/admin.ts). Invalidates "me" so the new mailbox shows up in the switcher/rail immediately. */
export function useProvisionMailbox() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: ProvisionMailboxRequest) => provisionMailbox(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["me"] });
    },
  });
}

export function usePermissions(mailboxId: string | undefined) {
  return useQuery({
    queryKey: mailboxQueryKeys.permissions(mailboxId ?? ""),
    queryFn: () => listPermissions(mailboxId!),
    enabled: Boolean(mailboxId),
  });
}

export function useGrantPermission(mailboxId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: GrantPermissionRequest) => grantPermission(mailboxId!, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: mailboxQueryKeys.permissions(mailboxId ?? "") });
    },
  });
}

export function useRevokePermission(mailboxId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => revokePermission(mailboxId!, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: mailboxQueryKeys.permissions(mailboxId ?? "") });
    },
  });
}

/**
 * Signature + out-of-office (Phase 9), owner-only. Invalidates "me" (not a
 * dedicated per-mailbox settings query — there isn't one) since that's the
 * one place signature/oooEnabled/oooMessage are actually read from
 * everywhere else in the app (the mailbox switcher, compose's signature
 * prefill), same reasoning useProvisionMailbox already invalidates it.
 */
export function useUpdateMailboxSettings(mailboxId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateMailboxSettingsRequest) => updateMailboxSettings(mailboxId!, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["me"] });
    },
  });
}

/** Owner-only, system-wide global accounts view (see routes/admin.ts). */
export function useAdminUsers() {
  return useQuery({
    queryKey: ["admin", "users"],
    queryFn: listAllUsers,
  });
}
